import { ProcurementCard } from './ProcurementCard'
import { useProcurement } from './useProcurement'

// Spalten der Beschaffungs-Pipeline; der Punkt-Farbton kommt aus den bestehenden Status-Klassen (--st-*).
const COLUMNS = [
  { status: 'needed', title: 'Zu bestellen', dot: 'wartet' },
  { status: 'ordered', title: 'Bestellt', dot: 'arbeit' },
  { status: 'delivered', title: 'Geliefert', dot: 'offen' },
  { status: 'installed', title: 'Verbaut', dot: 'fertig' },
] as const

/**
 * Einkauf: das reine Beschaffungs-Board. Hier laufen ausschließlich procurement_items — zwei Quellen:
 * Nachschub (restock, aus dem Inventar angestoßen) und Einmal-Teile (order_oneoff, aus Aufträgen).
 * Ein gelieferter Nachschub ist erledigt (ins Lager verbucht) und verschwindet vom Board; ein Einmal-Teil
 * wandert nach „Geliefert" weiter zu „Verbaut".
 */
export function EinkaufBoard() {
  const { data, isLoading, error } = useProcurement()

  if (isLoading) return <div className="muted">Einkauf wird geladen…</div>
  if (error) {
    return (
      <div className="error" role="alert">
        Einkauf konnte nicht geladen werden.
      </div>
    )
  }

  // Gelieferter Nachschub ist abgeschlossen (im Lager verbucht) → nicht mehr aufs Board.
  const items = (data?.items ?? []).filter((i) => !(i.source === 'restock' && i.status === 'delivered'))
  const needed = items.filter((i) => i.status === 'needed').length
  const ordered = items.filter((i) => i.status === 'ordered').length

  return (
    <section className="wf-view">
      <div>
        <h2 className="viewtitle">Einkauf</h2>
        <p className="sub">Beschaffung · Nachschub aus dem Inventar + Einmal-Teile aus Aufträgen</p>
      </div>

      <div className="wf-statrow">
        <div className="wf-stat wf-stat--wartet">
          <div className="wf-stat__value">{needed}</div>
          <div className="wf-stat__sub">Zu bestellen</div>
        </div>
        <div className="wf-stat wf-stat--arbeit">
          <div className="wf-stat__value">{ordered}</div>
          <div className="wf-stat__sub">In Bestellung</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Nichts zu beschaffen</div>
          <div className="wf-empty__text">
            Einträge entstehen im Inventar (kritische Teile → „Zu bestellen") oder am Auftrag (Einmal-Teile).
          </div>
        </div>
      ) : (
        <div className="wf-board">
          {COLUMNS.map((column) => {
            const colItems = items.filter((i) => i.status === column.status)
            return (
              <div key={column.status} className="wf-col">
                <div className="wf-col__head">
                  <span className={`wf-col__dot wf-col__dot--${column.dot}`} aria-hidden="true" />
                  <span className="wf-col__title">{column.title}</span>
                  <span className="wf-col__count">{colItems.length}</span>
                </div>
                {colItems.length === 0 ? (
                  <div className="wf-col__empty">Nichts</div>
                ) : (
                  colItems.map((item) => <ProcurementCard key={item.id} item={item} />)
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
